import { useTranslation } from "react-i18next";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { RadioButton } from "components/designV2/radioButton/radioButton";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import type { TimeReportsV2DisplayOption } from "web-apps/company/stores/timeReportsV2/timeReportsV2Store.reducer";
import type { TimeReportsGroupByOption } from "web-apps/company/components/timeReportsV2/components/toolbar/timeReportsToolbar.component";
import {
  RadioGroup,
  Section,
  SectionDescription,
  SectionTitle,
} from "./timeReportsOptionsPanel.styled";

interface TimeReportsOptionsPanelProps {
  readonly displayOption: TimeReportsV2DisplayOption;
  readonly groupByOption: TimeReportsGroupByOption;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onDisplayOptionChange: (value: TimeReportsV2DisplayOption) => void;
  readonly onGroupByChange: (value: TimeReportsGroupByOption) => void;
}

const DISPLAY_OPTIONS: readonly TimeReportsV2DisplayOption[] = [
  "simple",
  "detailed",
];

export const TimeReportsOptionsPanel = (
  props: TimeReportsOptionsPanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const isOpen = props.isOpen;
  const onClose = props.onClose;

  useCloseOnEscape({
    isOpen,
    onClose,
    stopPropagation: true,
  });

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title={t("TimeReports.Options")}
    >
      <Section>
        <SectionTitle>{t("TimeReports.GroupBy")}</SectionTitle>
        <SectionDescription>
          {t("TimeReports.GroupByDescription")}
        </SectionDescription>
        <RadioGroup role="radiogroup" aria-label={t("TimeReports.GroupBy")}>
          <RadioButton
            name="time-reports-group-by"
            value="team"
            label={t("TimeReports.Team")}
            checked={props.groupByOption === "team"}
            onChange={() => props.onGroupByChange("team")}
          />
          <RadioButton
            name="time-reports-group-by"
            value="person"
            label={t("TimeReports.Person")}
            checked={props.groupByOption === "person"}
            onChange={() => props.onGroupByChange("person")}
          />
        </RadioGroup>
      </Section>
      <Section>
        <SectionTitle>{t("TimeReports.DisplayOptions")}</SectionTitle>
        <RadioGroup
          role="radiogroup"
          aria-label={t("TimeReports.DisplayOptions")}
        >
          {DISPLAY_OPTIONS.map((option) => (
            <RadioButton
              key={option}
              name="time-reports-display-option"
              value={option}
              checked={props.displayOption === option}
              onChange={() => props.onDisplayOptionChange(option)}
              label={t(
                option === "simple"
                  ? "TimeReports.SimpleView"
                  : "TimeReports.DetailedView",
              )}
            />
          ))}
        </RadioGroup>
      </Section>
    </SidePanel>
  );
};
